import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  Snackbar,
  Alert,
  Modal,
  Fade,
  Backdrop,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import Cookies from "js-cookie";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import jsPDF from "jspdf";
import { useParams } from "react-router-dom";
import axios from "axios";
import { EditEmailPopup } from "./editEmailPopup";
import { useSelector } from "react-redux";

const genderTypes = {
  M: "Male",
  F: "Female",
  O: "Prefer Not To Say",
};

// ── Card wrapper ──
const SectionCard = ({ title, children, sx: sxProp = {} }) => (
  <Box
    sx={{
      border: "1px solid #c7d9f0",
      borderRadius: "10px",
      overflow: "hidden",
      mb: 2.5,
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      background: "#fff",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      ...sxProp,
    }}
  >
    {/* Card header — light blue, never scrolls */}
    <Box sx={{ background: "#dbeafe", px: 2, py: 1.2, flexShrink: 0 }}>
      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#1a2e4a" }}>
        {title}
      </Typography>
    </Box>

    {/* Card body */}
    <Box sx={{ px: 2, py: 1.5, flex: 1, overflowY: "auto", minHeight: 0 }}>
      {children}
    </Box>
  </Box>
);

// ── Reusable label + value row ──
const InfoRow = ({ label, children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
    <Typography
      sx={{
        color: "#8a9bb0",
        fontSize: "13px",
        fontWeight: 400,
        width: "44%",
        flexShrink: 0,
        pt: "1px",
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

const valueStyle = {
  fontWeight: 500,
  color: "#1a2e4a",
  fontSize: "13px",
  wordWrap: "break-word",
};

/**
 * layout="row"    → cards sit side-by-side (full-page view)
 * layout="column" → cards stack vertically  (sidebar drawer view)
 */
const CandidateInfo = ({
  candidatedetails,
  refreshCandidateDetails,
  resumeRef,
  resumeSectionRef,
  openResumePreviewSignal = 0,
  layout = "row", // "row" | "column"
}) => {
  const { id } = useParams();
  const PermissionStatus = useSelector((state) => state.user.permissions);
  const AddCandidatePermission = PermissionStatus["100"];

  const fileExt = `${candidatedetails?.resume}`.split(".").pop();
  const [boxHeight, setBoxHeight] = useState("25vh");
  const [additionalboxheight, setadditionalboxheight] = useState("25vh");
  const boxRef = useRef(null);
  const addRef = useRef(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);

  const isRow = layout === "row";
  const isColumn = layout === "column";

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });
  const handleOpenEmailDialog = () => setOpenEmailDialog(true);
  const handleCloseEmailDialog = () => setOpenEmailDialog(false);

  const handleSaveEmail = async (candidateId, newEmail, confirmEmail) => {
    try {
      const response = await axios.put(
        "/edit-email",
        { id: candidateId, new_email: newEmail, confirmed_email: confirmEmail },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      setSnackbar({
        open: true,
        message: response.data?.message || "Email updated successfully!",
        severity: "success",
      });
      setTimeout(async () => {
        await refreshCandidateDetails();
        handleCloseEmailDialog();
      }, 1500);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to update email",
        severity: "error",
      });
      throw error;
    }
  };

  const docs = [{ uri: `${candidatedetails?.resume_path}`, fileType: fileExt }];

  let label =
    candidatedetails?.current_loc?.length > 0
      ? candidatedetails?.current_loc[0]?.label
      : "NA";

  useEffect(() => {
    if (boxRef.current) {
      const contentHeight = boxRef.current.scrollHeight;
      setBoxHeight(contentHeight > 25 ? `${contentHeight}px` : "25vh");
    }
  }, [candidatedetails.cover_letter]);

  useEffect(() => {
    if (addRef.current) {
      const addcontentHeight = addRef.current.scrollHeight;
      setadditionalboxheight(
        addcontentHeight > 25 ? `${addcontentHeight}px` : "25vh",
      );
    }
  }, [candidatedetails.additional_info]);

  useEffect(() => {
    if (!openResumePreviewSignal || isColumn) return;
    if (candidatedetails?.resume_path) {
      setResumeModalOpen(true);
    }
  }, [
    openResumePreviewSignal,
    candidatedetails?.resume_path,
    isColumn,
  ]);

  const handleDownloadPDF = () => {
    const content = boxRef.current;
    var doc = new jsPDF({
      unit: "mm",
      format: "a4",
      orientation: "portrait",
      marginLeft: 10,
      marginRight: 10,
      marginTop: 10,
      marginBottom: 10,
    });
    doc.setFontSize(14);
    const lineHeight = 5;
    const textcontent = content.innerText.replace(/\n{3}/g, "");
    const textcontents = textcontent.replace("Sincerely,\n", "Sincerely,");
    const lines = doc.splitTextToSize(textcontents, 180);
    lines.forEach((line, index) => {
      doc.text(line, 10, 10 + index * lineHeight);
    });
    const fileName = candidatedetails.name
      ? `${candidatedetails.name}_${id}_CoverLetter.pdf`
      : "default_coverLetter.pdf";
    doc.save(fileName);
  };

  const handleDownloadFile = async () => {
    try {
      const response = await fetch(candidatedetails.resume_path);
      if (!response.ok)
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      const fileContent = await response.text();
      const blob = new Blob([fileContent], { type: "text/plain" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = candidatedetails.name
        ? `${candidatedetails.name}_Resume.txt`
        : "defaultResume.txt";
      link.click();
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  // ── Layout-driven styles ──────────────────────────────────────────────────
  const renderResumeDocument = () => {
    if (!candidatedetails?.resume_path) return null;
    if (fileExt === "pdf" || fileExt === "txt" || fileExt === "doc") {
      return (
        <iframe
          src={candidatedetails.resume_path}
          referrerPolicy="no-referrer-when-downgrade"
          width="100%"
          height="100%"
          title={candidatedetails.resume || "Resume"}
          style={{ border: 0, display: "block", minHeight: 0 }}
        />
      );
    }
    return (
      <DocViewer
        documents={docs}
        pluginRenderers={DocViewerRenderers}
        style={{ height: "100%", width: "100%", minHeight: 0 }}
        config={{
          header: { disableHeader: true, disableFileName: true },
        }}
        theme={{ disableThemeScrollbar: true }}
      />
    );
  };

  // Outer wrapper for the three cards
  const cardsContainerSx = isRow
    ? {
        // Full-page: horizontal, equal-width columns, stretch to fill height
        display: "flex",
        flexDirection: "row",
        gap: 2,
        alignItems: "stretch",
        flexWrap: "wrap",
        height: "100%",
      }
    : {
        // Sidebar: vertical stack, no fixed height
        display: "flex",
        flexDirection: "column",
        gap: 0,
      };

  // Per-card sx overrides
  const cardSx = isRow
    ? { flex: "1 1 280px", minWidth: 260, height: "100%", mb: 0 }
    : { width: "100%", mb: 2.5 };

  return (
    <Box
      ref={resumeRef}
      sx={{
        overflow: "hidden",
        height: isRow ? "100%" : "auto",
        pr: 0.5,
        pt: isRow ? "12px" : 0,
      }}
    >
      {/* ══ Three info cards ══ */}
      <Box sx={cardsContainerSx}>
        {/* ══ Basic Details Card ══ */}
        <SectionCard title="Basic Details" sx={cardSx}>
          <InfoRow label="Full Name">
            <Typography sx={{ ...valueStyle, textTransform: "capitalize" }}>
              {candidatedetails.name || "NA"}
            </Typography>
          </InfoRow>

          <InfoRow label="Gender">
            <Typography sx={valueStyle}>
              {candidatedetails.gender
                ? genderTypes[candidatedetails.gender]
                : "NA"}
            </Typography>
          </InfoRow>

          <InfoRow label="Date of Birth">
            <Typography sx={valueStyle}>
              {candidatedetails?.dob
                ? new Date(candidatedetails?.dob).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "NA"}
            </Typography>
          </InfoRow>

          <InfoRow label="Email">
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography sx={valueStyle}>
                {candidatedetails.email || "NA"}
              </Typography>
              <IconButton
                size="small"
                sx={{
                  padding: "3px",
                  color: "#3F95EA",
                  "&:hover": { backgroundColor: "#EFF6FF" },
                }}
                aria-label="edit"
                onClick={handleOpenEmailDialog}
              >
                <EditIcon sx={{ fontSize: "14px" }} />
              </IconButton>
            </Stack>
          </InfoRow>

          {candidatedetails?.contacts
            ?.filter(
              (contact) =>
                contact.contact_type === "email" && contact.is_primary === 0,
            )
            .map((contact, index) => (
              <InfoRow
                key={contact.conctact_id}
                label={`Alternate Email ${index + 1}`}
              >
                <Typography sx={valueStyle}>{contact.contact}</Typography>
              </InfoRow>
            ))}

          <InfoRow label="Mobile">
            <Typography sx={valueStyle}>
              {candidatedetails.mobile ? `+1 ${candidatedetails.mobile}` : "NA"}
            </Typography>
          </InfoRow>

          <InfoRow label="Current Address">
            <Typography sx={valueStyle}>
              {candidatedetails.address || "NA"}
            </Typography>
          </InfoRow>

          <InfoRow label="Work Authorization">
            <Typography sx={valueStyle}>
              {(() => {
                const workAuth =
                  candidatedetails?.work_authorization?.work_authorization;
                const workAuthText = candidatedetails?.work_authorization_text;
                if (
                  workAuth === "not available" ||
                  workAuth === "Not Available"
                )
                  return "NA";
                if (workAuth === "Others" && workAuthText)
                  return `${workAuthText}`;
                return workAuth || "NA";
              })()}
            </Typography>
          </InfoRow>
        </SectionCard>

        {/* ══ Professional Details Card ══ */}
        <SectionCard title="Professional Details" sx={cardSx}>
          <InfoRow label="Total Experience">
            <Typography sx={valueStyle}>
              {!(candidatedetails?.year * 1) &&
              !(candidatedetails?.month * 1) ? (
                "NA"
              ) : (
                <>
                  {candidatedetails?.year * 1 > 1
                    ? `${candidatedetails?.year} years`
                    : candidatedetails?.year * 1 === 1
                      ? `${candidatedetails?.year} year`
                      : ""}
                  &nbsp;
                  {candidatedetails?.month * 1 > 1
                    ? `${candidatedetails?.month} months`
                    : candidatedetails?.month * 1 === 1
                      ? `${candidatedetails?.month} month`
                      : ""}
                </>
              )}
            </Typography>
          </InfoRow>

          <InfoRow label="Current Employer">
            <Typography
              sx={{
                ...valueStyle,
                textTransform: "capitalize",
                wordBreak: "break-all",
              }}
            >
              {candidatedetails.current_employer || "NA"}
            </Typography>
          </InfoRow>

          <InfoRow label="Current Salary">
            <Typography sx={valueStyle}>
              {candidatedetails.CTC ? `$ ${candidatedetails.CTC}` : "NA"}
            </Typography>
          </InfoRow>

          <InfoRow label="Expected Salary">
            <Typography sx={valueStyle}>
              {candidatedetails.Exp_CTC
                ? `$ ${candidatedetails.Exp_CTC}`
                : "NA"}
            </Typography>
          </InfoRow>

          <InfoRow label="Current Location">
            <Typography sx={valueStyle}>
              {candidatedetails?.current_loc?.length === 0 ? "NA" : label}
            </Typography>
          </InfoRow>

          <InfoRow label="Preferred Location">
            <Typography sx={valueStyle}>
              {candidatedetails?.preferred_loc?.length > 0
                ? candidatedetails.preferred_loc
                    .map((item) =>
                      item.label === "Any Location()"
                        ? "Any Location"
                        : item.label || "NA",
                    )
                    .join(", ")
                : "NA"}
            </Typography>
          </InfoRow>

          <InfoRow label="Ready to Relocate">
            <Typography sx={valueStyle}>
              {candidatedetails?.preferred_loc?.length > 0
                ? "Ready for preferred location"
                : "NA"}
            </Typography>
          </InfoRow>

          <InfoRow label="Radius">
            <Typography sx={valueStyle}>
              {candidatedetails?.radius !== undefined &&
              candidatedetails?.radius !== null
                ? `${candidatedetails.radius} ${candidatedetails.radius === 1 ? "Mile" : "Miles"}`
                : "NA"}
            </Typography>
          </InfoRow>

          <InfoRow label="Notice Period">
            <Typography sx={valueStyle}>
              {candidatedetails?.notice_period !== undefined &&
              candidatedetails?.notice_period !== null
                ? candidatedetails.notice_period === 0
                  ? "Immediate Joiner"
                  : `${candidatedetails.notice_period} ${
                      candidatedetails.notice_period === 1 ? "Day" : "Days"
                    }`
                : "NA"}
            </Typography>
          </InfoRow>

          <InfoRow label="Last Working Day">
            <Typography sx={valueStyle}>
              {candidatedetails?.last_working_day
                ? new Date(
                    candidatedetails.last_working_day,
                  ).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                  })
                : "NA"}
            </Typography>
          </InfoRow>

          <InfoRow label="Expected Joining">
            <Typography sx={valueStyle}>
              {candidatedetails?.expected_joining_date
                ? new Date(
                    candidatedetails.expected_joining_date,
                  ).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                  })
                : "NA"}
            </Typography>
          </InfoRow>
        </SectionCard>

        {/* ══ Other Details Card ══ */}
        <SectionCard title="Other Details" sx={cardSx}>
          <InfoRow label="Highest Education">
            <Typography sx={valueStyle}>
              {candidatedetails?.highest_educ?.highest_education ===
                "not available" ||
              candidatedetails?.highest_educ?.highest_education ===
                "Not Available"
                ? "NA"
                : candidatedetails?.highest_educ?.highest_education || "NA"}
            </Typography>
          </InfoRow>

          <InfoRow label="Skills">
            <Box display="flex" gap={1} flexWrap="wrap">
              {candidatedetails?.skill?.length > 0 ? (
                candidatedetails.skill.map((item, idx) => (
                  <Chip
                    key={idx}
                    label={item.label}
                    sx={{
                      textTransform: "capitalize",
                      background: "rgb(219, 234, 254)",
                      fontSize: "12px",
                      height: "26px",
                    }}
                    variant="filled"
                  />
                ))
              ) : (
                <Typography sx={valueStyle}>NA</Typography>
              )}
            </Box>
          </InfoRow>

          <InfoRow label="LinkedIn">
            <Typography
              sx={{
                width: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "13px",
              }}
            >
              {candidatedetails.linkedIn ? (
                <a
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    color: "#0A66C2",
                    fontWeight: "400",
                    fontSize: "13px",
                  }}
                  href={
                    candidatedetails.linkedIn.startsWith("http")
                      ? candidatedetails.linkedIn
                      : `https://${candidatedetails.linkedIn}`
                  }
                  title={candidatedetails.linkedIn}
                >
                  {candidatedetails.linkedIn}
                </a>
              ) : (
                "NA"
              )}
            </Typography>
          </InfoRow>

          <InfoRow label="Portfolio Link">
            <Typography
              sx={{
                width: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "13px",
              }}
            >
              {candidatedetails.portfolio ? (
                <a
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    color: "#0A66C2",
                    fontWeight: "400",
                    fontSize: "13px",
                  }}
                  href={
                    candidatedetails.portfolio.startsWith("http")
                      ? candidatedetails.portfolio
                      : `https://${candidatedetails.portfolio}`
                  }
                  title={candidatedetails.portfolio}
                >
                  {candidatedetails.portfolio}
                </a>
              ) : (
                "NA"
              )}
            </Typography>
          </InfoRow>

          <Box sx={{ mb: 1 }} id="resumescrol" ref={resumeSectionRef}>
            <Typography
              sx={{
                color: "#8a9bb0",
                fontSize: "13px",
                fontWeight: 400,
                mb: 1,
              }}
            >
              Resume
            </Typography>

            {candidatedetails.resume_path &&
            candidatedetails.resume_path !== "" ? (
              isColumn ? (
                <Box
                  sx={{
                    border: "1px solid #e8edf2",
                    borderRadius: "8px",
                    overflow: "hidden",
                    bgcolor: "#fff",
                    height: "min(75vh, 820px)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {renderResumeDocument()}
                </Box>
              ) : (
                <Box
                  onClick={() => setResumeModalOpen(true)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    border: "1px solid #e8edf2",
                    borderRadius: "12px",
                    px: 2,
                    py: 1.5,
                    background: "#f8fafc",
                    "&:hover": { background: "#f1f5f9" },
                    transition: "background 0.15s",
                    cursor: "pointer",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: 44,
                      height: 52,
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 52,
                        background: "#e8edf5",
                        borderRadius: "6px",
                        border: "1px solid #d0daea",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        pb: 0.8,
                        gap: 0.5,
                        overflow: "hidden",
                      }}
                    >
                      {[70, 85, 70].map((w, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: `${w}%`,
                            height: "3px",
                            background: "#b0bec5",
                            borderRadius: "2px",
                          }}
                        />
                      ))}
                    </Box>
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        background: "#ef4444",
                        borderRadius: "4px 0 4px 0",
                        px: 0.6,
                        py: 0.3,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "7px",
                          fontWeight: 800,
                          color: "#fff",
                          letterSpacing: "0.3px",
                          lineHeight: 1,
                        }}
                      >
                        {fileExt?.toUpperCase() || "PDF"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#1a2e4a",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {candidatedetails.resume || "Resume"}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "12px", color: "#94a3b8", mt: 0.3 }}
                    >
                      {fileExt?.toUpperCase()} file
                    </Typography>
                  </Box>
                </Box>
              )
            ) : (
              <Box
                sx={{
                  border: "1px solid #e8edf2",
                  borderRadius: "12px",
                  px: 2,
                  py: 1.5,
                  background: "#f8fafc",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <InsertDriveFileOutlinedIcon
                  sx={{ fontSize: "18px", color: "#c0ccd8" }}
                />
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#94a3b8",
                    fontStyle: "italic",
                  }}
                >
                  No resume found
                </Typography>
              </Box>
            )}
          </Box>

          {candidatedetails.additional_info && (
            <InfoRow label="Additional Info">
              <Box
                sx={{
                  minHeight: additionalboxheight,
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: "4px",
                  overflow: "auto",
                  p: 1,
                  fontSize: "13px",
                }}
              >
                <div
                  ref={addRef}
                  dangerouslySetInnerHTML={{
                    __html: candidatedetails.additional_info.replace(
                      /\n/g,
                      "<br>",
                    ),
                  }}
                />
              </Box>
            </InfoRow>
          )}
        </SectionCard>
      </Box>
      {/* ── end cards container ── */}

      {/* ══ Cover Letter Card ══ */}
      {candidatedetails?.cover_letter !== null &&
        candidatedetails?.cover_letter?.trim() !== "" && (
          <SectionCard
            title={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>Cover Letter</span>
                {AddCandidatePermission?.add === 1 && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDownloadPDF}
                    startIcon={<FileDownloadIcon />}
                    sx={{ fontSize: "12px", textTransform: "none" }}
                  >
                    Download
                  </Button>
                )}
              </Box>
            }
          >
            <Box
              id="coverletterscrol"
              sx={{
                minHeight: boxHeight,
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: "4px",
                overflow: "auto",
                p: 1,
              }}
            >
              <div
                ref={boxRef}
                className="cover-letter-content"
                dangerouslySetInnerHTML={{
                  __html: candidatedetails.cover_letter,
                }}
              />
            </Box>
          </SectionCard>
        )}

      {/* ══ Resume Preview Modal ══ */}
      <Modal
        open={resumeModalOpen}
        onClose={() => setResumeModalOpen(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 200 } }}
      >
        <Fade in={resumeModalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95vw", md: "80vw" },
              height: { xs: "90vh", md: "88vh" },
              background: "#fff",
              borderRadius: "10px",
              boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Modal Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2.5,
                py: 1.5,
                background: "#dbeafe",
                borderBottom: "1px solid #bfdbfe",
              }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <InsertDriveFileOutlinedIcon
                  sx={{ color: "#1d4ed8", fontSize: "20px" }}
                />
                <Typography
                  sx={{ fontWeight: 600, fontSize: "14px", color: "#1e3a5f" }}
                >
                  {candidatedetails.resume || "Resume"}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" gap={0.5}>
                {AddCandidatePermission?.add === 1 && (
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      sx={{
                        color: "#1d4ed8",
                        "&:hover": { background: "#bfdbfe" },
                      }}
                      onClick={
                        fileExt === "txt" ? handleDownloadFile : undefined
                      }
                      component={fileExt !== "txt" ? "a" : undefined}
                      href={
                        fileExt !== "txt"
                          ? candidatedetails.resume_path
                          : undefined
                      }
                      download={
                        fileExt !== "txt"
                          ? candidatedetails.name
                            ? `${candidatedetails.name}_Resume.${fileExt}`
                            : `Resume.${fileExt}`
                          : undefined
                      }
                      target={fileExt !== "txt" ? "_blank" : undefined}
                    >
                      <FileDownloadIcon sx={{ fontSize: "20px" }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Close">
                  <IconButton
                    size="small"
                    onClick={() => setResumeModalOpen(false)}
                    sx={{
                      color: "#475569",
                      "&:hover": { background: "#bfdbfe" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: "20px" }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {/* Modal Body — resume viewer */}
            <Box sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
              {renderResumeDocument()}
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* ══ Dialogs & Snackbar ══ */}
      <EditEmailPopup
        open={openEmailDialog}
        onClose={handleCloseEmailDialog}
        onSave={handleSaveEmail}
        candidateId={id}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CandidateInfo;
